/**
 * API Health Monitor
 * Checkt of API keys nog geldig zijn en stuurt waarschuwingen
 */

import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

interface APIHealthCheck {
  service: 'openai' | 'gemini' | 'elevenlabs';
  status: 'healthy' | 'degraded' | 'down';
  error?: string;
  timestamp: Date;
  responseTime?: number;
}

class APIHealthMonitor {
  private lastCheckTime: { [key: string]: number } = {};
  private checkInterval = 3600000; // 1 uur
  private adminEmail = 'abdul@superkonnected.nl'; // Jouw email

  /**
   * Check OpenAI API health
   */
  async checkOpenAI(): Promise<APIHealthCheck> {
    const startTime = Date.now();
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;

    if (!apiKey) {
      console.warn('⚠️ OpenAI API key niet geconfigureerd');
      return {
        service: 'openai',
        status: 'down',
        error: 'API key niet geconfigureerd',
        timestamp: new Date()
      };
    }

    try {
      const response = await fetch('https://api.openai.com/v1/models', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiKey}`
        }
      });

      const responseTime = Date.now() - startTime;

      if (response.ok) {
        return {
          service: 'openai',
          status: 'healthy',
          timestamp: new Date(),
          responseTime
        };
      } else if (response.status === 401) {
        return {
          service: 'openai',
          status: 'down',
          error: `401 Unauthorized - API key is expired of invalid (key: ${apiKey.substring(0, 20)}...)`,
          timestamp: new Date(),
          responseTime
        };
      } else if (response.status === 429) {
        return {
          service: 'openai',
          status: 'degraded',
          error: 'Rate limit bereikt',
          timestamp: new Date(),
          responseTime
        };
      } else {
        return {
          service: 'openai',
          status: 'degraded',
          error: `HTTP ${response.status}`,
          timestamp: new Date(),
          responseTime
        };
      }
    } catch (error: any) {
      return {
        service: 'openai',
        status: 'down',
        error: error.message || 'Network error',
        timestamp: new Date()
      };
    }
  }

  /**
   * Check Gemini API health
   */
  async checkGemini(): Promise<APIHealthCheck> {
    const startTime = Date.now();
    const apiKey = process.env.REACT_APP_GOOGLE_AI_API_KEY;

    if (!apiKey) {
      return {
        service: 'gemini',
        status: 'down',
        error: 'API key niet geconfigureerd',
        timestamp: new Date()
      };
    }

    try {
      // Test met nieuwste model (gemini-2.5-flash)
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash?key=${apiKey}`,
        { method: 'GET' }
      );

      const responseTime = Date.now() - startTime;

      if (response.ok) {
        return {
          service: 'gemini',
          status: 'healthy',
          timestamp: new Date(),
          responseTime
        };
      } else if (response.status === 400 || response.status === 403 || response.status === 404) {
        return {
          service: 'gemini',
          status: 'down',
          error: `${response.status} - API key is invalid or model not available (key: ${apiKey.substring(0, 20)}...)`,
          timestamp: new Date(),
          responseTime
        };
      } else {
        return {
          service: 'gemini',
          status: 'degraded',
          error: `HTTP ${response.status}`,
          timestamp: new Date(),
          responseTime
        };
      }
    } catch (error: any) {
      return {
        service: 'gemini',
        status: 'down',
        error: error.message || 'Network error',
        timestamp: new Date()
      };
    }
  }

  /**
   * Check ElevenLabs API health
   */
  async checkElevenLabs(): Promise<APIHealthCheck> {
    const startTime = Date.now();
    const apiKey = process.env.REACT_APP_ELEVENLABS_API_KEY;

    if (!apiKey) {
      console.warn('⚠️ ElevenLabs API key niet geconfigureerd');
      return {
        service: 'elevenlabs',
        status: 'down',
        error: 'API key niet geconfigureerd',
        timestamp: new Date()
      };
    }

    try {
      const response = await fetch('https://api.elevenlabs.io/v1/voices', {
        method: 'GET',
        headers: {
          'xi-api-key': apiKey
        }
      });

      const responseTime = Date.now() - startTime;

      if (response.ok) {
        return {
          service: 'elevenlabs',
          status: 'healthy',
          timestamp: new Date(),
          responseTime
        };
      } else if (response.status === 401) {
        return {
          service: 'elevenlabs',
          status: 'down',
          error: `401 Unauthorized - API key is invalid (key: ${apiKey.substring(0, 20)}...)`,
          timestamp: new Date(),
          responseTime
        };
      } else {
        return {
          service: 'elevenlabs',
          status: 'degraded',
          error: `HTTP ${response.status}`,
          timestamp: new Date(),
          responseTime
        };
      }
    } catch (error: any) {
      return {
        service: 'elevenlabs',
        status: 'down',
        error: error.message || 'Network error',
        timestamp: new Date()
      };
    }
  }

  /**
   * Log health check naar Firebase voor tracking
   */
  private async logHealthCheck(check: APIHealthCheck) {
    try {
      await addDoc(collection(db, 'apiHealthLogs'), {
        ...check,
        timestamp: serverTimestamp(),
        environment: process.env.NODE_ENV
      });
    } catch (error) {
      console.error('Failed to log health check:', error);
    }
  }

  /**
   * Stuur notificatie als API down is
   */
  private async notifyAPIDown(check: APIHealthCheck) {
    console.error(`🚨 API DOWN: ${check.service.toUpperCase()}`, check.error);
    
    // Log naar Firebase
    await this.logHealthCheck(check);

    // Browser notificatie (als permissions zijn gegeven)
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`⚠️ ${check.service.toUpperCase()} API Down`, {
        body: check.error || 'API is niet beschikbaar',
        icon: '/logo192.png',
        tag: `api-down-${check.service}`,
        requireInteraction: true
      });
    }

    // TODO: Eventueel email notificatie via Firebase Function
  }

  /**
   * Run all health checks
   */
  async runHealthChecks(): Promise<void> {
    const now = Date.now();
    
    // Check of we niet te vaak checken (max 1x per uur)
    if (this.lastCheckTime['all'] && now - this.lastCheckTime['all'] < this.checkInterval) {
      return;
    }

    console.log('🏥 Running API health checks...');
    this.lastCheckTime['all'] = now;

    const [openai, gemini, elevenlabs] = await Promise.all([
      this.checkOpenAI(),
      this.checkGemini(),
      this.checkElevenLabs()
    ]);

    // Log results
    console.log('OpenAI:', openai.status, openai.error || '✅', `${openai.responseTime}ms`);
    console.log('Gemini:', gemini.status, gemini.error || '✅', `${gemini.responseTime}ms`);
    console.log('ElevenLabs:', elevenlabs.status, elevenlabs.error || '✅', `${elevenlabs.responseTime}ms`);

    // Notify als API down is
    if (openai.status === 'down') await this.notifyAPIDown(openai);
    if (gemini.status === 'down') await this.notifyAPIDown(gemini);
    if (elevenlabs.status === 'down') await this.notifyAPIDown(elevenlabs);

    // Log alles naar Firebase voor historische tracking
    await Promise.all([
      this.logHealthCheck(openai),
      this.logHealthCheck(gemini),
      this.logHealthCheck(elevenlabs)
    ]);
  }

  /**
   * Check een specifieke API on-demand (voor debugging)
   */
  async checkAPI(service: 'openai' | 'gemini' | 'elevenlabs'): Promise<APIHealthCheck> {
    switch (service) {
      case 'openai':
        return this.checkOpenAI();
      case 'gemini':
        return this.checkGemini();
      case 'elevenlabs':
        return this.checkElevenLabs();
    }
  }

  /**
   * Request notification permissions
   */
  async requestNotificationPermission(): Promise<void> {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }
}

export const apiHealthMonitor = new APIHealthMonitor();

// Auto-run health checks alleen als REACT_APP_ENABLE_API_MONITORING === 'true'
// Dit voorkomt onnodige 401 errors in development
if (typeof window !== 'undefined' && process.env.REACT_APP_ENABLE_API_MONITORING === 'true') {
  console.log('📊 API Health Monitoring enabled');
  // First check after 10 seconds (after app loads)
  setTimeout(() => {
    apiHealthMonitor.runHealthChecks();
  }, 10000);
  
  // Then check elke 2 uur
  setInterval(() => {
    apiHealthMonitor.runHealthChecks();
  }, 7200000); // 2 uur
} else {
  console.log('📊 API Health Monitoring disabled (set REACT_APP_ENABLE_API_MONITORING=true to enable)');
}
